'use client';

import NextImage, { type ImageProps, type StaticImageData } from 'next/image';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { IMAGE_PLACEHOLDER } from '@/constants';

const CDN_HOST = 'cdn.welfarehello.com';
const loadedImageSources = new Set<string>();

/** 외부 이미지 (CDN이 아닌) 여부 확인 */
const isExternalNonCdnImage = (
  src: string | StaticImageData | undefined,
): boolean => {
  if (!src || typeof src !== 'string') return false;
  if (!src.startsWith('http://') && !src.startsWith('https://')) return false;

  try {
    const url = new URL(src);

    return !url.hostname.includes(CDN_HOST);
  } catch {
    return false;
  }
};

const getImageCacheKey = (src: string | StaticImageData | undefined) => {
  if (!src) return;

  return typeof src === 'string' ? src : src.src;
};

interface CustomImageBaseProps extends Omit<
  ImageProps,
  'src' | 'className' | 'onError' | 'onLoad' | 'width' | 'height' | 'fill'
> {
  src?: string | StaticImageData;
  alt: string;
  fallbackSrc?: string;
  className?: string;
  onError?: (error: React.SyntheticEvent<HTMLImageElement>) => void;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

type CustomImageProps =
  | (CustomImageBaseProps & {
      fill: true;
      width?: number;
      height?: number;
    })
  | (CustomImageBaseProps & {
      fill?: false;
      width: number;
      height: number;
    });

export const Image = memo(
  ({
    src,
    alt,
    fallbackSrc = IMAGE_PLACEHOLDER,
    quality = 50,
    loading = 'lazy',
    className,
    onError,
    onLoad,
    preload,
    unoptimized,
    ...props
  }: CustomImageProps) => {
    const shouldPreload = preload;
    const [hasError, setHasError] = useState(false);

    // src 또는 fallback을 표시할 소스 결정
    const displaySrc = useMemo(
      () => (hasError ? fallbackSrc : src || fallbackSrc),
      [hasError, src, fallbackSrc],
    );
    const cacheKey = useMemo(() => getImageCacheKey(displaySrc), [displaySrc]);
    const hasLoadedBefore = cacheKey ? loadedImageSources.has(cacheKey) : false;

    // 외부 이미지는 최적화 건너뛰기 (커스텀 loader가 width를 적용하지 않음)
    const shouldUnoptimize = useMemo(
      () => unoptimized ?? isExternalNonCdnImage(displaySrc),
      [unoptimized, displaySrc],
    );

    const handleError = useCallback(
      (error: React.SyntheticEvent<HTMLImageElement>) => {
        if (!hasError) {
          setHasError(true);
          onError?.(error);
        }
      },
      [hasError, onError],
    );

    const handleLoad = useCallback(
      (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (cacheKey) {
          loadedImageSources.add(cacheKey);
        }

        onLoad?.(event);
      },
      [cacheKey, onLoad],
    );

    const resolvedLoading =
      preload || loading !== 'lazy' || !hasLoadedBefore ? loading : 'eager';

    // src 변경 시 에러 상태 리셋
    useEffect(() => {
      setHasError(false);
    }, [src]);

    return (
      <NextImage
        quality={quality}
        loading={shouldPreload ? undefined : resolvedLoading}
        preload={shouldPreload}
        // next 16의 preload는 <link rel=preload>만 내보내고 img의 fetchpriority를 빠뜨린다
        fetchPriority={shouldPreload ? 'high' : undefined}
        unoptimized={shouldUnoptimize}
        {...props}
        alt={alt}
        className={className}
        src={displaySrc}
        onError={handleError}
        onLoad={handleLoad}
      />
    );
  },
);

Image.displayName = 'Image';
